// ADMIN / DEVELOPMENT TEST MODE: reference-answer panel.
//
// Shows, for one stage, three clearly separated things:
//   EXPECTED ANSWER       — the authored reference (commands, expected state, verification)
//   ACTUAL LEARNER INPUT  — what was really typed / done in this lab during that stage
//   VALIDATION RESULT     — the real checks evaluated against the live lab (read-only)
// plus a TEST RESULT banner that says whether learner validation was performed.
//
// Reference answers are loaded with a dynamic import so they live in their own
// chunk and are only fetched when an admin asks for them. Nothing here is written
// to learner progress; the panel itself is not persisted at all.

import { h } from './h.js';
import { clone } from '../engine/net/model.js';
import { isHost, dev } from '../engine/net/sim.js';
import { IosSession } from '../engine/ios/cli.js';
import { WinShell } from '../engine/pc/winshell.js';

export const BYPASS_TEXT = 'Admin test bypass — learner validation was not performed.';

let answersMod = null;
export async function loadAnswers() {
  if (!answersMod) answersMod = await import('../admin/answers.js');
  return answersMod;
}
/** The answers module if it has already been loaded (sync), else null. */
export const loadedAnswers = () => answersMod;

/** Prompts for each reference command, computed by replaying on a throwaway copy. */
export function withPrompts(rt, cmds) {
  const net = clone(rt.net);
  const shells = {};
  return cmds.map(([d, line]) => {
    try {
      if (!shells[d]) {
        const node = dev(net, d);
        const st = clone(rt.state.sessions[d] || {});
        shells[d] = isHost(node) ? new WinShell(net, d, Object.keys(st).length ? st : undefined) : new IosSession(net, d, Object.keys(st).length ? st : undefined);
      }
      const prompt = shells[d].prompt();
      shells[d].exec(line);
      return { d, line, prompt };
    } catch { return { d, line, prompt: `${d}>` }; }
  });
}

/** What the learner actually did during a stage (from the lab event log). */
export function learnerInput(rt, stageId) {
  return rt.state.log.filter((e) => e.stage === stageId && (e.type === 'cmd' || (e.type === 'action' && !['verify', 'hint'].includes(e.name))))
    .map((e) => (e.type === 'cmd' ? `${e.device}: ${e.line || '(enter)'}` : `[action] ${e.name}${e.id ? ` ${e.id}` : ''}`));
}

/** Live, side-effect-free evaluation of the stage's real checks. */
export function liveValidation(rt, stage) {
  const blockers = rt.blockers(stage);
  const tasks = (stage.tasks || []).map((t) => { let ok = false; try { ok = !!t.check(rt); } catch { ok = false; } return { text: t.text, ok }; });
  return { blockers, tasks, ok: !blockers.length && tasks.every((t) => t.ok) };
}

const list = (items, cls = '') => (items.length ? h('ul', { class: `aa-list ${cls}` }, items.map((x) => h('li', {}, x))) : h('p', { class: 'muted small' }, 'None recorded.'));

/**
 * Render the panel. `mode`:
 *   'view'     — admin opened the answer; nothing was bypassed
 *   'bypass'   — Pass stage (skip checks) / Mark complete (test)
 *   'replay'   — reference answer was executed and the real validation ran (`replayResult`)
 */
export function answerPanel(rt, stage, ans, { mode = 'view', replayResult = null, onClose } = {}) {
  const val = liveValidation(rt, stage);
  const cmdRows = withPrompts(rt, ans.cmds);
  const testResult = mode === 'bypass'
    ? h('div', { class: 'aa-banner warn', 'data-testid': 'admin-bypass-warning', role: 'status' }, h('b', {}, BYPASS_TEXT), h('span', {}, ' “Mark complete (test)” / “Pass stage” records a test completion in the admin sandbox only. It does not mean the lab was technically validated.'))
    : mode === 'replay'
      ? h('div', { class: `aa-banner ${replayResult?.ok ? 'ok' : 'bad'}`, 'data-testid': 'admin-replay-result' }, h('b', {}, replayResult?.ok ? 'Reference answer replayed — real validation PASSED.' : 'Reference answer replayed — real validation did NOT pass.'),
        replayResult?.blockers?.length ? h('div', { class: 'small' }, `Still blocking: ${replayResult.blockers.map((b) => b.text).join('; ')}`) : null,
        replayResult?.results?.filter((r) => !r.ok).length ? h('div', { class: 'small' }, `Failed checks: ${replayResult.results.filter((r) => !r.ok).map((r) => r.text).join('; ')}`) : null)
      : h('div', { class: 'aa-banner info' }, h('b', {}, 'Reference answer shown to the admin only.'), h('span', {}, ' No validation was skipped or performed by opening it.'));

  return h('section', { class: 'admin-answer', 'data-testid': 'admin-answer', 'data-mode': mode, 'aria-label': 'Admin reference answer' },
    h('div', { class: 'aa-head' }, h('div', { class: 'eyebrow admin-eyebrow' }, 'ADMIN ONLY · NOT VISIBLE TO LEARNERS'), h('b', {}, `Stage: ${stage.phase} — ${stage.title}`),
      onClose ? h('button', { class: 'btn ghost small', 'data-testid': 'admin-answer-close', onclick: onClose }, 'Hide') : null),
    h('div', { class: 'aa-sec test', 'data-testid': 'aa-test' }, h('div', { class: 'aa-title' }, 'TEST RESULT'), testResult),
    h('div', { class: 'aa-sec expected', 'data-testid': 'aa-expected' },
      h('div', { class: 'aa-title' }, 'EXPECTED ANSWER'),
      ans.authored ? null : h('p', { class: 'muted small' }, 'No authored command answer for this stage — expected state comes from the stage checks.'),
      ans.notes ? h('p', { class: 'small' }, ans.notes) : null,
      h('div', { class: 'aa-sub' }, 'Commands'),
      cmdRows.length ? h('pre', { class: 'aa-cmds', 'data-testid': 'aa-cmds' }, cmdRows.map((c) => `${c.prompt.startsWith(c.d) ? '' : `[${c.d}] `}${c.prompt} ${c.line}`).join('\n')) : h('p', { class: 'muted small' }, 'No console commands — non-console activity only.'),
      ans.apply || ans.after ? h('p', { class: 'small' }, 'Includes a physical / GUI step (applied automatically on replay).') : null,
      ans.activity.length ? [h('div', { class: 'aa-sub' }, 'Activity answers'), list(ans.activity)] : null,
      h('div', { class: 'aa-sub' }, 'Expected state'), list(ans.expect),
      h('div', { class: 'aa-sub' }, 'Verification'), list(ans.verify)),
    h('div', { class: 'aa-sec actual', 'data-testid': 'aa-actual' },
      h('div', { class: 'aa-title' }, 'ACTUAL LEARNER INPUT (this lab, this stage)'),
      list(learnerInput(rt, stage.id), 'mono')),
    h('div', { class: 'aa-sec validation', 'data-testid': 'aa-validation' },
      h('div', { class: 'aa-title' }, 'VALIDATION RESULT (live state, read-only)'),
      val.blockers.length ? [h('div', { class: 'aa-sub' }, 'Blocking evidence / activities'), list(val.blockers.map((b) => `✗ ${b.text}`))] : null,
      h('div', { class: 'aa-sub' }, 'Checks'), list(val.tasks.map((t) => `${t.ok ? '✓' : '✗'} ${t.text}`)),
      h('p', { class: `small ${val.ok ? 'ok-text' : 'muted'}` }, val.ok ? 'Current lab state satisfies this stage.' : 'Current lab state does not satisfy this stage.')));
}

/** Execute the reference answer through the real consoles, then run the real validation. */
export function replayAnswer(rt, ans) {
  if (ans.apply) { ans.apply(rt); rt.action('admin-reference-apply'); }
  for (const [d, line] of ans.cmds) rt.exec(d, line);
  if (ans.after) { ans.after(rt); rt.action('admin-reference-apply'); }
  return rt.verify();
}

/** Small modal used by the dashboard (Mark complete (test) for a unit not opened). */
export function infoModal(title, ...kids) {
  const close = () => wrap.remove();
  const wrap = h('div', { class: 'modal-wrap', 'data-testid': 'admin-result', onclick: (e) => { if (e.target === wrap) close(); } },
    h('div', { class: 'modal wide', role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
      h('h3', {}, title), h('div', { class: 'modal-body' }, ...kids),
      h('div', { class: 'modal-actions' }, h('button', { class: 'btn primary', 'data-testid': 'admin-result-close', onclick: close }, 'Close'))));
  wrap.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  document.body.append(wrap);
  wrap.querySelector('button').focus();
  return wrap;
}
